import { createContext, useContext, useState, useEffect } from "react";

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    try {
      const saved = localStorage.getItem("user");
      if (saved) {
        setUser(JSON.parse(saved));
      }
    } catch (e) {
      console.error("Error reading stored user:", e);
    }
    setLoading(false);
  }, []);

  const login = (emailOrUser, name, role = "admin", id = null, permissions = [], extra = {}) => {
    let newUser;
    if (typeof emailOrUser === "object" && emailOrUser !== null) {
      newUser = {
        id: emailOrUser.id,
        email: emailOrUser.email,
        name: emailOrUser.username || emailOrUser.name || "User",
        username: emailOrUser.username || emailOrUser.name || "User",
        role: (emailOrUser.role || "ADMIN").toUpperCase(),
        status: emailOrUser.status || "active",
        parent_id: emailOrUser.parent_id || null,
        qr_code: emailOrUser.qr_code || null,
        referalcode: emailOrUser.referalcode || null,
        permissions: Array.isArray(emailOrUser.permissions) ? emailOrUser.permissions : [],
      };
    } else {
      newUser = {
        id,
        email: emailOrUser,
        name: name || "Admin",
        username: name || "Admin",
        role: (role || "ADMIN").toUpperCase(),
        permissions: Array.isArray(permissions) ? permissions : [],
        ...extra,
      };
    }

    setUser(newUser);
    localStorage.setItem("user", JSON.stringify(newUser));
  };

  const logout = () => {
    setUser(null);
    localStorage.removeItem("user");
    localStorage.removeItem("token");
  };

  // Helper methods for RBAC checks in components
  const hasRole = (...roles) => {
    if (!user) return false;
    const currentRole = (user.role || "").toUpperCase();
    if (currentRole === "SUPER_ADMIN") return true;
    return roles.map(r => r.toUpperCase()).includes(currentRole);
  };

  const hasPermission = (...perms) => {
    if (!user) return false;
    const currentRole = (user.role || "").toUpperCase();
    if (currentRole === "SUPER_ADMIN") return true;

    const userPerms = user.permissions || [];
    if (userPerms.includes("*")) return true;

    return perms.every(p => userPerms.includes(p));
  };

  const isSuperAdmin = user?.role?.toUpperCase() === "SUPER_ADMIN";
  const isAdmin = user?.role?.toUpperCase() === "ADMIN";
  const isBackOffice = user?.role?.toUpperCase() === "BACK_OFFICE";
  const isAgent = user?.role?.toUpperCase() === "AGENT";

  return (
    <AuthContext.Provider
      value={{
        user,
        login,
        logout,
        loading,
        hasRole,
        hasPermission,
        isSuperAdmin,
        isAdmin,
        isBackOffice,
        isAgent,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
